import React, { FC, useRef } from 'react'
import Heading from '../../atoms/Heading/Heading';
import classNames from 'classnames';
import { motion, useInView } from 'framer-motion';
import DynamicSVG from '../../atoms/DynamicSvg/DynamicSvg';
import { getImage } from '../../../app/utlis/imageConstants';

interface IdeaProjectsProps {
    iosData: any
}

const IdeaProjects: FC<IdeaProjectsProps> = ({ iosData }) => {
    // Ref for scroll animation
    const containerRef = useRef(null);

    // Use inView hook for scroll animation
    const isContainerInView = useInView(containerRef, { once: false, amount: 0.2 });

    // Create a fixed set of refs instead of using map
    // This is the correct way to handle multiple elements with hooks
    const row0Ref = useRef(null);
    const row1Ref = useRef(null);
    const row2Ref = useRef(null);
    const row3Ref = useRef(null);
    const row4Ref = useRef(null);

    // Individual inView states for each row
    const isRow0InView = useInView(row0Ref, { once: false, amount: 0.1 });
    const isRow1InView = useInView(row1Ref, { once: false, amount: 0.1 });
    const isRow2InView = useInView(row2Ref, { once: false, amount: 0.1 });
    const isRow3InView = useInView(row3Ref, { once: false, amount: 0.1 });
    const isRow4InView = useInView(row4Ref, { once: false, amount: 0.1 });

    // Function to get correct ref based on index
    const getRefForIndex = (index: number) => {
        switch (index) {
            case 0: return row0Ref;
            case 1: return row1Ref;
            case 2: return row2Ref;
            case 3: return row3Ref;
            case 4: return row4Ref;
            default: return row0Ref;
        }
    };

    // Function to get correct inView state based on index
    const getInViewForIndex = (index: number) => {
        switch (index) {
            case 0: return isRow0InView;
            case 1: return isRow1InView;
            case 2: return isRow2InView;
            case 3: return isRow3InView;
            case 4: return isRow4InView;
            default: return isRow0InView;
        }
    };

    // Animation variants
    const containerVariant = {
        hidden: { opacity: 0.6 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                staggerChildren: 0.2
            }
        }
    };

    const fadeInVariant = {
        hidden: { opacity: 0, y: 0 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.2,
                ease: "easeOut"
            }
        }
    };

    const imageVariant = {
        hidden: { opacity: 1, scale: 0.95 },
        visible: {
            opacity: 1,
            scale: 1,
            transition: {
                duration: 0.5,
                ease: "easeOut",
                delay: 0.2
            }
        }
    };

    return (
        <motion.div
            ref={containerRef}
            initial="hidden"
            animate={isContainerInView ? "visible" : "hidden"}
            variants={containerVariant}
            className="px-4 md:px-0 pt-10 md:pt-0"
        >
            <Heading title='TURNING AN IDEA INTO A REALITY' subTitle='The way we develop our projects' />
            <div className='flex flex-col gap-8 md:gap-16 pt-8'>
                {iosData.IOSIdeaProjectsData.map((projects: any, index: number) => {
                    let img = getImage(projects.image ?? "") ?? "";
                    return (
                        <motion.div
                            className={classNames(
                                'flex h-[550px] md:h-[250px] lg:h-[300px] xl:h-[400px] 2xl:h-[600px] items-center justify-center px-0 md:px-5 lg:px-16 gap-6 md:gap-2 py-8 md:py-0',
                                projects.row
                            )}
                            initial="hidden"
                            whileInView="visible"
                            ref={getRefForIndex(index)}
                            viewport={{ once: true, amount: 0.3 }}
                            variants={fadeInVariant}
                        >
                            <motion.div
                                className={`w-full md:w-[55%] px-2 md:px-0 flex justify-end ${index % 2 !== 0 ? "items-end" : "items-start"} flex-col`}
                                initial={{ opacity: 1, x: projects.row === 'row' ? -10 : 10 }}
                                animate={getInViewForIndex(index) ?
                                    { opacity: 1, x: 0, transition: { delay: 0.2, duration: 0.2 } } :
                                    { opacity: 1, x: projects.row === 'row' ? -10 : 10 }
                                }
                            >
                                <div className={classNames('font-semibold text-xl md:text-2xl mb-2', `text-${projects.text}`)}>{projects.title}</div>
                                <div className={classNames('w-full md:w-[80%] text-greyQuaternary text-base md:text-lg', `text-${projects.text}`)}>{projects.subtitle}</div>
                            </motion.div>
                            {/* <motion.img variants={imageVariant} src={projects.image} className={classNames('w-full md:w-[30%]', projects.justify)} /> */}
                            <DynamicSVG color='#fff' size='w-[90%]   md:w-[30%]'>{img}</DynamicSVG>
                        </motion.div>
                    )
                })}
            </div>
        </motion.div>
    )
}

export default IdeaProjects
